/**
 * HR: employees, pay (how it's figured and how it got there), the pay plan, positions,
 * evaluations, and vehicle inspections.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { api, fileToBase64 } from '../shared/api.ts';
import { useAuth } from '../shared/auth.tsx';
import { useRouter } from '../shared/router.tsx';
import { Badge, Card, ErrorBox, FileDrop, Notice, PageHeader, Spinner, useAction, useLoad } from '../shared/ui.tsx';
import { at, day, EmptyState, KPI, when } from './common.tsx';
import { ShutoffStatus } from './Shutoffs.tsx';

type Tone = 'ok' | 'warn' | 'bad' | 'neutral' | 'info';
const money = (n: number | null | undefined, cents = false) => (n == null ? '—' : n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: cents ? 2 : 0, minimumFractionDigits: cents ? 2 : 0 }));
const crumb = (label: string, to: string, navigate: (p: string) => void) => (
  <button className="btn link" onClick={() => navigate(at(to))}>
    {label}
  </button>
);
const STATUS: Record<string, string> = { active: 'Active', leave: 'On leave', light_duty: 'Light duty', military: 'Military leave', suspended: 'Suspended', separated: 'Separated' };

// ─── Employees ──────────────────────────────────────────────────────────────
export function HrEmployees() {
  const { navigate } = useRouter();
  const [q, setQ] = useState('');
  const r = useLoad(() => api.get<any>(`/api/hr/employees?q=${encodeURIComponent(q.trim())}`), [q]);
  return (
    <div className="ops stack">
      <PageHeader
        crumb="HR"
        title="Employees"
        sub="Rank, assignment, years of service and pay for everyone."
        actions={
          <>
            <button className="btn" onClick={() => navigate(at('/hr/positions'))}>
              Positions
            </button>
            <button className="btn" onClick={() => navigate(at('/hr/pay-plan'))}>
              Pay plan
            </button>
          </>
        }
      />
      {r.data && (
        <div className="ops-kpis">
          <KPI n={r.data.totals.count} label="Employees" />
          <KPI n={money(r.data.totals.payroll)} label="Annual base payroll" />
        </div>
      )}
      <input type="search" className="rp-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, rank, unit, employee #…" aria-label="Search employees" />
      {r.error ? (
        <ErrorBox error={r.error} />
      ) : !r.data ? (
        <Spinner />
      ) : (
        <div className="card table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Rank</th>
                <th>Unit</th>
                <th>Hired</th>
                <th>Pay</th>
                <th>Last evaluation</th>
              </tr>
            </thead>
            <tbody>
              {r.data.employees.map((e: any) => (
                <tr key={e.id} className="clickable" onClick={() => navigate(at(`/hr/employees/${e.id}`))}>
                  <td>
                    <strong>{e.name}</strong>
                    <div className="small muted">{[e.employee_number && `#${e.employee_number}`, e.badge_number, e.status && e.status !== 'active' && STATUS[e.status]].filter(Boolean).join(' · ')}</div>
                  </td>
                  <td>{e.rank ?? e.title ?? '—'}</td>
                  <td>{e.unit ?? '—'}</td>
                  <td>
                    {e.hire_date ? day(e.hire_date) : '—'}
                    {e.years != null && <div className="small muted">{e.years} yrs</div>}
                  </td>
                  <td>
                    {e.pay ? (
                      <>
                        <strong>{money(e.pay.total)}</strong>
                        <div className="small muted">
                          {e.pay.gradeName ? `${e.pay.gradeName}, step ${e.pay.step}` : 'Set amount'} · {money(e.pay.hourly, true)}/hr
                        </div>
                      </>
                    ) : (
                      <span className="muted">Not set</span>
                    )}
                  </td>
                  <td>{e.last_eval ? day(e.last_eval) : <Badge tone="warn">None on file</Badge>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function HrEmployee({ id }: { id?: string }) {
  const { navigate } = useRouter();
  const r = useLoad(() => api.get<any>(id ? `/api/hr/employees/${id}` : '/api/hr/me'), [id]);
  const plan = useLoad(() => (id ? api.get<any>('/api/hr/pay-plan').catch(() => null) : Promise.resolve(null)), [id]);
  const [editPay, setEditPay] = useState(false);
  const [editRec, setEditRec] = useState(false);
  const v = r.data;
  if (!v) return <div className="ops stack">{r.error ? <ErrorBox error={r.error} /> : <Spinner />}</div>;
  const e = v.employee;
  const rec = v.record ?? {};
  const p = v.pay;
  return (
    <div className="ops stack">
      <PageHeader crumb={id ? crumb('Employees', '/hr', navigate) : 'Me'} title={id ? e.name : 'My pay & record'} sub={[rec.rank ?? e.title, e.unit, e.badge_number].filter(Boolean).join(' · ')} />
      {id && rec.status === 'separated' && <ShutoffStatus kind="employee" id={id} />}
      <div className="grid2 top">
        <Card title="Employee" actions={v.canEdit && <button className="btn small" onClick={() => setEditRec(!editRec)}>{editRec ? 'Close' : 'Edit'}</button>}>
          {editRec ? (
            <RecordForm id={e.id} rec={rec} onSaved={() => (setEditRec(false), r.reload())} />
          ) : (
            <dl className="kv">
              <dt>Employee #</dt>
              <dd>{rec.employee_number ?? '—'}</dd>
              <dt>Rank</dt>
              <dd>{rec.rank ?? '—'}</dd>
              <dt>Assignment</dt>
              <dd>{rec.assignment ?? '—'}</dd>
              <dt>Shift / unit</dt>
              <dd>{e.unit ?? '—'}</dd>
              <dt>Access cards</dt>
              <dd>{[rec.school_card && `School${rec.school_card_number ? ` #${rec.school_card_number}` : ''}`, rec.county_card && `County${rec.county_card_number ? ` #${rec.county_card_number}` : ''}`].filter(Boolean).join(' · ') || '—'}</dd>
              <dt>Hired</dt>
              <dd>
                {rec.hire_date ? day(rec.hire_date) : '—'}
                {v.years != null && <span className="muted"> · {v.years} years</span>}
              </dd>
              <dt>Sworn</dt>
              <dd>{rec.sworn_date ? day(rec.sworn_date) : '—'}</dd>
              <dt>Status</dt>
              <dd>{STATUS[rec.status ?? 'active']}</dd>
            </dl>
          )}
        </Card>
        <Card title="Pay — how it’s figured" actions={v.canEdit && plan.data && <button className="btn small" onClick={() => setEditPay(!editPay)}>{editPay ? 'Close' : 'Change pay'}</button>}>
          {editPay && plan.data ? (
            <PayForm id={e.id} pay={p} plan={plan.data} onSaved={() => (setEditPay(false), r.reload())} />
          ) : !p ? (
            <p className="muted small">Pay isn’t set up yet.</p>
          ) : (
            <div className="pay-fig">
              <div className="pay-line">
                <span>{p.gradeName ? `${p.gradeName}, step ${p.step} of ${p.steps}` : 'Base pay'}</span>
                <strong>{money(p.base)}</strong>
              </div>
              {p.supplements.map((s: any) => (
                <div key={s.name} className="pay-line">
                  <span>
                    + {s.name} <span className="small muted">({s.kind === 'percent' ? `${s.amount}% of base` : 'flat'})</span>
                  </span>
                  <strong>{money(s.value)}</strong>
                </div>
              ))}
              <div className="pay-line total">
                <span>Annual</span>
                <strong>{money(p.total)}</strong>
              </div>
              <div className="small muted">
                {money(p.hourly, true)} an hour at {p.hoursPerYear.toLocaleString()} hours a year · since {day(p.effective_on)}
              </div>
            </div>
          )}
        </Card>
      </div>
      <Card title="How they got here">
        {v.sinceStart && (
          <div className="pay-journey">
            <div>
              <span className="small muted">Started at</span>
              <strong>{money(v.sinceStart.start)}</strong>
            </div>
            <span className="pay-arrow" aria-hidden>
              →
            </span>
            <div>
              <span className="small muted">Now</span>
              <strong>{money(v.sinceStart.now)}</strong>
            </div>
            <div>
              <span className="small muted">Change</span>
              <strong className={v.sinceStart.change >= 0 ? 'pay-up' : 'pay-down'}>
                {v.sinceStart.change >= 0 ? '+' : ''}
                {money(v.sinceStart.change)} ({v.sinceStart.pct}%)
              </strong>
            </div>
          </div>
        )}
        {!v.history.length ? (
          <p className="small muted">No pay history yet.</p>
        ) : (
          <ol className="timeline pay-tl">
            {v.history.map((h: any, i: number) => (
              <li key={i}>
                <span className="tl-dot" aria-hidden />
                <div>
                  <strong>
                    {v.reasons[h.reason] ?? h.reason}: {money(h.total)}
                  </strong>{' '}
                  {h.change != null && (
                    <span className={h.change >= 0 ? 'pay-up small' : 'pay-down small'}>
                      ({h.change >= 0 ? '+' : ''}
                      {money(h.change)}
                      {h.pct != null ? `, ${h.pct}%` : ''})
                    </span>
                  )}
                  <div className="small">
                    {h.grade_name ? `${h.grade_name}, step ${h.step}` : 'Set amount'} · base {money(h.base)}
                    {(h.supplements ?? []).length > 0 && ` · ${(h.supplements as any[]).map((s) => `${s.name} ${money(s.value)}`).join(', ')}`}
                  </div>
                  {h.note && <div className="small muted">{h.note}</div>}
                  <div className="small muted">
                    Effective {day(h.effective_on)} · entered by {h.by_name}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        )}
      </Card>
      {v.assignments.length > 0 && (
        <Card title="Assignment history">
          <ul className="pio-hist">
            {v.assignments.map((a: any, i: number) => (
              <li key={i}>
                <strong>
                  {a.change}: {a.from_value ? `${a.from_value} → ` : ''}
                  {a.to_value ?? '—'}
                </strong>
                <span className="small muted">
                  {' '}
                  · {day(a.effective_on)}
                  {a.by_name ? ` · ${a.by_name}` : ''}
                </span>
                {a.note && <div className="small">{a.note}</div>}
              </li>
            ))}
          </ul>
        </Card>
      )}
      <Card title="Evaluations">
        {!v.evaluations.length ? (
          <p className="small muted">None on file.</p>
        ) : (
          <ul className="pio-hist">
            {v.evaluations.map((x: any) => (
              <li key={x.id}>
                <button className="btn link" onClick={() => navigate(at(`/evaluations/${x.id}`))}>
                  {x.number} · {x.kind} {x.period_end ? `(through ${day(x.period_end)})` : ''}
                </button>
                <span className="small muted">
                  {' '}
                  · {x.evaluator} · overall {x.overall ?? '—'} · {x.status.replace('_', ' ')}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function RecordForm({ id, rec, onSaved }: { id: string; rec: any; onSaved: () => void }) {
  const [f, setF] = useState({ employeeNumber: rec.employee_number ?? '', sccjaNumber: rec.sccja_number ?? '', rank: rec.rank ?? '', hireDate: rec.hire_date ?? '', swornDate: rec.sworn_date ?? '', status: rec.status ?? 'active', assignment: rec.assignment ?? '', notes: rec.notes ?? '', effectiveOn: new Date().toLocaleDateString('en-CA'), note: '' });
  const [cards, setCards] = useState({ schoolCard: !!rec.school_card, schoolCardNumber: rec.school_card_number ?? '', countyCard: !!rec.county_card, countyCardNumber: rec.county_card_number ?? '' });
  const a = useAction();
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const save = (e: FormEvent) => {
    e.preventDefault();
    if (f.status === 'separated' && rec.status !== 'separated' && (cards.schoolCard || cards.countyCard) && !window.confirm(`Mark separated? This emails ${[cards.schoolCard && 'the school district (school access card)', cards.countyCard && 'the county (building access card)'].filter(Boolean).join(' and ')} to shut it off.`)) return;
    a.run(async () => (await api.post(`/api/hr/employees/${id}`, { ...Object.fromEntries(Object.entries(f).map(([k, v]) => [k, v || null])), ...cards, schoolCardNumber: cards.schoolCardNumber || null, countyCardNumber: cards.countyCardNumber || null }), onSaved()));
  };
  return (
    <form className="stack sm" onSubmit={save}>
      <ErrorBox error={a.error} />
      <div className="grid-2">
        <label>
          Employee #
          <input value={f.employeeNumber} onChange={set('employeeNumber')} />
        </label>
        <label>
          SCCJA officer #
          <input value={f.sccjaNumber} onChange={set('sccjaNumber')} placeholder="Printed on traffic tickets" />
        </label>
        <label>
          Rank
          <input value={f.rank} onChange={set('rank')} placeholder="Deputy I, Sergeant…" />
        </label>
        <label>
          Assignment
          <input value={f.assignment} onChange={set('assignment')} placeholder="Patrol, CID, SRO…" />
        </label>
        <label>
          Status
          <select value={f.status} onChange={set('status')}>
            {Object.entries(STATUS).map(([k, l]) => (
              <option key={k} value={k}>
                {l}
              </option>
            ))}
          </select>
        </label>
        <label>
          Hire date
          <input type="date" value={f.hireDate} onChange={set('hireDate')} />
        </label>
        <label>
          Sworn date
          <input type="date" value={f.swornDate} onChange={set('swornDate')} />
        </label>
        <label>
          Changes effective
          <input type="date" value={f.effectiveOn} onChange={set('effectiveOn')} />
        </label>
        <label>
          Why (for the history)
          <input value={f.note} onChange={set('note')} />
        </label>
      </div>
      <fieldset className="hr-cards">
        <legend>Building access cards (shut off when they leave)</legend>
        <div className="grid-2">
          <label className="check">
            <input type="checkbox" checked={cards.schoolCard} onChange={(e) => setCards({ ...cards, schoolCard: e.target.checked })} /> School access card
          </label>
          {cards.schoolCard && (
            <label>
              School card #
              <input value={cards.schoolCardNumber} onChange={(e) => setCards({ ...cards, schoolCardNumber: e.target.value })} />
            </label>
          )}
          <label className="check">
            <input type="checkbox" checked={cards.countyCard} onChange={(e) => setCards({ ...cards, countyCard: e.target.checked })} /> County building access card
          </label>
          {cards.countyCard && (
            <label>
              County card #
              <input value={cards.countyCardNumber} onChange={(e) => setCards({ ...cards, countyCardNumber: e.target.value })} />
            </label>
          )}
        </div>
      </fieldset>
      <div>
        <button className="btn primary" disabled={a.busy}>
          Save
        </button>
      </div>
    </form>
  );
}

function PayForm({ id, pay, plan, onSaved }: { id: string; pay: any; plan: any; onSaved: () => void }) {
  const grades = plan.grades.filter((g: any) => !g.archived);
  const [f, setF] = useState({ gradeId: pay?.grade_id ?? grades[0]?.id ?? '', step: String(pay?.step ?? 1), supplementIds: (pay?.supplement_ids ?? []) as string[], baseOverride: pay?.base_override != null ? String(pay.base_override) : '', effectiveOn: new Date().toLocaleDateString('en-CA'), reason: pay ? 'step' : 'hire', note: '', percent: '' });
  const a = useAction();
  const g = grades.find((x: any) => x.id === f.gradeId);
  const base = f.baseOverride ? Number(f.baseOverride) : g ? Number(g.steps[Number(f.step) - 1] ?? 0) : 0;
  const pct = Number(f.percent);
  const raised = pct > 0 ? Math.round(base * (1 + pct / 100) * 100) / 100 : null;
  const save = (e: FormEvent) => {
    e.preventDefault();
    a.run(async () => (await api.post(`/api/hr/employees/${id}/pay`, { gradeId: f.gradeId || null, step: f.gradeId ? Number(f.step) : null, supplementIds: f.supplementIds, baseOverride: f.baseOverride ? Number(f.baseOverride) : null, percent: pct > 0 ? pct : null, effectiveOn: f.effectiveOn, reason: f.reason, note: f.note || null }), onSaved()));
  };
  return (
    <form className="stack sm" onSubmit={save}>
      <ErrorBox error={a.error} />
      <div className="grid-2">
        <label>
          Pay grade
          <select value={f.gradeId} onChange={(e) => setF({ ...f, gradeId: e.target.value, step: '1' })}>
            <option value="">No grade — set the base</option>
            {grades.map((x: any) => (
              <option key={x.id} value={x.id}>
                {x.name}
              </option>
            ))}
          </select>
        </label>
        {g ? (
          <label>
            Step
            <select value={f.step} onChange={(e) => setF({ ...f, step: e.target.value })}>
              {g.steps.map((s: number, i: number) => (
                <option key={i} value={i + 1}>
                  Step {i + 1} — {money(s)}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <label>
            Base pay (annual)
            <input type="number" min={0} step={0.01} value={f.baseOverride} onChange={(e) => setF({ ...f, baseOverride: e.target.value })} />
          </label>
        )}
        <label>
          Raise by %
          <input type="number" min={0.1} max={50} step={0.1} value={f.percent} onChange={(e) => setF({ ...f, percent: e.target.value, reason: e.target.value && f.reason === 'step' ? 'cola' : f.reason })} placeholder="e.g. 3 for a 3% COLA" />
          {raised != null && (
            <span className="small muted">
              Base {money(base)} → <strong>{money(raised)}</strong> (+{money(raised - base)})
            </span>
          )}
        </label>
        <label>
          Why
          <select value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })}>
            {Object.entries(plan.reasons).map(([k, l]: any) => (
              <option key={k} value={k}>
                {l}
              </option>
            ))}
          </select>
        </label>
        <label>
          Effective
          <input type="date" value={f.effectiveOn} onChange={(e) => setF({ ...f, effectiveOn: e.target.value })} />
        </label>
      </div>
      {plan.supplements.filter((s: any) => !s.archived).length > 0 && (
        <fieldset className="pay-sups">
          <legend className="small muted">Supplements</legend>
          {plan.supplements
            .filter((s: any) => !s.archived)
            .map((s: any) => (
              <label key={s.id} className="check">
                <input type="checkbox" checked={f.supplementIds.includes(s.id)} onChange={(e) => setF({ ...f, supplementIds: e.target.checked ? [...f.supplementIds, s.id] : f.supplementIds.filter((x) => x !== s.id) })} /> {s.name} ({s.kind === 'percent' ? `${s.amount}%` : money(s.amount)})
              </label>
            ))}
        </fieldset>
      )}
      <label>
        Note
        <input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
      </label>
      <div>
        <button className="btn primary" disabled={a.busy}>
          Save pay change
        </button>
      </div>
    </form>
  );
}

// ─── Pay plan ───────────────────────────────────────────────────────────────
export function PayPlan() {
  const { navigate } = useRouter();
  const r = useLoad(() => api.get<any>('/api/hr/pay-plan'), []);
  const a = useAction();
  const [g, setG] = useState({ id: '', name: '', steps: '' });
  const [s, setS] = useState({ id: '', name: '', kind: 'flat', amount: '' });
  const [cola, setCola] = useState({ percent: '', effectiveOn: new Date().toLocaleDateString('en-CA'), note: '' });
  const [done, setDone] = useState('');
  const v = r.data;
  return (
    <div className="ops stack">
      <PageHeader crumb={crumb('Employees', '/hr', navigate)} title="Pay plan" sub="Pay grades with their steps, and the supplements added on top (certifications, degrees, special assignments)." />
      <ErrorBox error={r.error ?? a.error} />
      {done && <Notice tone="ok">{done}</Notice>}
      {v && (
        <>
          <Card title="Pay grades and steps">
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Grade</th>
                    {Array.from({ length: Math.max(1, ...v.grades.map((x: any) => x.steps.length)) }, (_, i) => (
                      <th key={i}>Step {i + 1}</th>
                    ))}
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {v.grades.map((x: any) => (
                    <tr key={x.id} className={x.archived ? 'muted' : ''}>
                      <td>
                        <strong>{x.name}</strong>
                      </td>
                      {Array.from({ length: Math.max(1, ...v.grades.map((y: any) => y.steps.length)) }, (_, i) => (
                        <td key={i}>{x.steps[i] != null ? money(x.steps[i]) : ''}</td>
                      ))}
                      <td>
                        <button className="btn link small" onClick={() => setG({ id: x.id, name: x.name, steps: x.steps.join(', ') })}>
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <form
              className="row gap wrap"
              style={{ marginTop: 10 }}
              onSubmit={(e) => {
                e.preventDefault();
                a.run(async () => (await api.post('/api/hr/pay-plan/grades', { id: g.id || null, name: g.name, steps: g.steps.split(/[,\s]+/).filter(Boolean).map((x) => Number(x.replace(/[$,]/g, ''))) }), setG({ id: '', name: '', steps: '' }), r.reload()));
              }}
            >
              <input value={g.name} onChange={(e) => setG({ ...g, name: e.target.value })} placeholder="Grade, e.g. Deputy I" aria-label="Grade name" required />
              <input value={g.steps} onChange={(e) => setG({ ...g, steps: e.target.value })} placeholder="Steps, e.g. 42000, 43260, 44558" aria-label="Steps" required style={{ flex: 1, minWidth: 260 }} />
              <button className="btn">{g.id ? 'Save grade' : 'Add grade'}</button>
            </form>
          </Card>
          <Card title="Supplements">
            <ul className="tr-cats">
              {v.supplements.map((x: any) => (
                <li key={x.id} className={x.archived ? 'muted' : ''}>
                  <span>
                    <strong>{x.name}</strong> — {x.kind === 'percent' ? `${x.amount}% of base` : `${money(x.amount)} a year`}
                  </span>
                  <button className="btn link small" onClick={() => setS({ id: x.id, name: x.name, kind: x.kind, amount: String(x.amount) })}>
                    Edit
                  </button>
                </li>
              ))}
            </ul>
            <form
              className="row gap wrap"
              style={{ marginTop: 10 }}
              onSubmit={(e) => {
                e.preventDefault();
                a.run(async () => (await api.post('/api/hr/pay-plan/supplements', { id: s.id || null, name: s.name, kind: s.kind, amount: Number(s.amount) }), setS({ id: '', name: '', kind: 'flat', amount: '' }), r.reload()));
              }}
            >
              <input value={s.name} onChange={(e) => setS({ ...s, name: e.target.value })} placeholder="e.g. FTO stipend, Associate’s degree" aria-label="Supplement name" required />
              <select value={s.kind} onChange={(e) => setS({ ...s, kind: e.target.value })} aria-label="Supplement kind">
                <option value="flat">$ a year</option>
                <option value="percent">% of base</option>
              </select>
              <input type="number" min={0} step={0.01} value={s.amount} onChange={(e) => setS({ ...s, amount: e.target.value })} placeholder="Amount" aria-label="Supplement amount" required />
              <button className="btn">{s.id ? 'Save supplement' : 'Add supplement'}</button>
            </form>
          </Card>
          <Card title="Cost-of-living raise">
            <form
              className="row gap wrap"
              onSubmit={(e) => {
                e.preventDefault();
                if (!confirm(`Raise every step and everyone’s pay by ${cola.percent}%?`)) return;
                a.run(async () => {
                  const res = await api.post<any>('/api/hr/pay-plan/cola', { percent: Number(cola.percent), effectiveOn: cola.effectiveOn, note: cola.note || null });
                  setDone(`Raised the pay plan ${cola.percent}% and recorded it for ${res.employees} employees.`);
                  r.reload();
                });
              }}
            >
              <label>
                Percent
                <input type="number" min={0.1} max={25} step={0.1} value={cola.percent} onChange={(e) => setCola({ ...cola, percent: e.target.value })} required />
              </label>
              <label>
                Effective
                <input type="date" value={cola.effectiveOn} onChange={(e) => setCola({ ...cola, effectiveOn: e.target.value })} />
              </label>
              <label style={{ flex: 1 }}>
                Note
                <input value={cola.note} onChange={(e) => setCola({ ...cola, note: e.target.value })} placeholder="FY27 county COLA" />
              </label>
              <button className="btn primary">Apply to everyone</button>
            </form>
          </Card>
        </>
      )}
    </div>
  );
}

// ─── Positions ──────────────────────────────────────────────────────────────
export function PositionsPage() {
  const { navigate } = useRouter();
  const r = useLoad(() => api.get<any>('/api/hr/positions'), []);
  const a = useAction();
  const [f, setF] = useState({ unitId: '', title: '', authorized: '' });
  const v = r.data;
  return (
    <div className="ops stack">
      <PageHeader crumb={crumb('Employees', '/hr', navigate)} title="Positions" sub="How many of each rank every shift and unit is authorized, and who fills them. Filled = people in that unit whose rank matches." />
      <ErrorBox error={r.error ?? a.error} />
      {v && (
        <>
          <div className="ops-kpis">
            <KPI n={v.totals.authorized} label="Authorized positions" />
            <KPI n={v.totals.vacant} label="Vacant" tone={v.totals.vacant ? 'warn' : undefined} />
          </div>
          {v.units.map((u: any) => (
            <Card key={u.id} title={`${u.name} · ${u.members} ${u.members === 1 ? 'person' : 'people'}`}>
              {!u.positions.length ? (
                <p className="small muted">No positions set.</p>
              ) : (
                <ul className="tr-cats">
                  {u.positions.map((p: any) => (
                    <li key={p.id}>
                      <span>
                        <strong>{p.title}</strong> — {p.filled} of {p.authorized} {p.vacant > 0 && <Badge tone="warn">{p.vacant} vacant</Badge>} {p.over > 0 && <Badge tone="info">{p.over} over</Badge>}
                        {p.people.length > 0 && <div className="small muted">{p.people.join(', ')}</div>}
                      </span>
                      {v.canEdit && (
                        <button className="btn link small" onClick={() => a.run(async () => (await api.post('/api/hr/positions', { id: p.id, remove: true }), r.reload()))}>
                          Remove
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          ))}
          {v.canEdit && (
            <Card title="Add a position">
              <form
                className="row gap wrap"
                onSubmit={(e) => {
                  e.preventDefault();
                  a.run(async () => (await api.post('/api/hr/positions', { unitId: f.unitId, title: f.title, authorized: Number(f.authorized) }), setF({ ...f, title: '', authorized: '' }), r.reload()));
                }}
              >
                <select value={f.unitId} onChange={(e) => setF({ ...f, unitId: e.target.value })} required aria-label="Unit">
                  <option value="">Unit…</option>
                  {v.units.map((u: any) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </select>
                <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Rank, e.g. Deputy I" required aria-label="Position" />
                <input type="number" min={0} max={500} value={f.authorized} onChange={(e) => setF({ ...f, authorized: e.target.value })} placeholder="How many" required aria-label="Authorized" />
                <button className="btn">Add</button>
              </form>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

// ─── Evaluations ────────────────────────────────────────────────────────────
const EV_STATUS: Record<string, [string, Tone]> = { draft: ['Draft', 'neutral'], with_employee: ['With the employee', 'warn'], acknowledged: ['Signed by the employee', 'info'], final: ['Final', 'ok'] };

export function EvaluationsPage() {
  const { navigate } = useRouter();
  const { can } = useAuth();
  const [scope, setScope] = useState<'written' | 'due' | 'all'>('written');
  const r = useLoad(() => api.get<any>(`/api/evaluations?scope=${scope}`), [scope]);
  const a = useAction();
  const start = (employeeId: string) =>
    a.run(async () => {
      const y = new Date().getFullYear();
      const res = await api.post<any>('/api/evaluations', { employeeId, kind: 'annual', periodStart: `${y - 1}-${new Date().toISOString().slice(5, 10)}`, periodEnd: new Date().toLocaleDateString('en-CA') });
      navigate(at(`/evaluations/${res.id}`));
    });
  return (
    <div className="ops stack">
      <PageHeader crumb="HR" title="Evaluations" sub="Rate, comment, send to the employee to sign, then a second-level review makes it final." />
      <ErrorBox error={a.error} />
      <div className="seg-ctl" role="tablist" aria-label="Which evaluations">
        {(['written', 'due', ...(can('hr.manage') ? ['all'] : [])] as const).map((k) => (
          <button key={k} type="button" role="tab" aria-selected={scope === k} className={scope === k ? 'on' : ''} onClick={() => setScope(k as any)}>
            {{ written: 'I’m writing', due: 'Due (none in a year)', all: 'All' }[k as 'written']}
          </button>
        ))}
      </div>
      {r.error ? (
        <ErrorBox error={r.error} />
      ) : !r.data ? (
        <Spinner />
      ) : scope === 'due' ? (
        !r.data.due.length ? (
          <EmptyState icon="check" title="Everyone has an evaluation from the last year" />
        ) : (
          <div className="card table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Hired</th>
                  <th>Last evaluation</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {r.data.due.map((d: any) => (
                  <tr key={d.id}>
                    <td>{d.name}</td>
                    <td>{d.hire_date ? day(d.hire_date) : '—'}</td>
                    <td>{d.last_eval ? day(d.last_eval) : 'Never'}</td>
                    <td>
                      {d.draft_id ? (
                        <button className="btn small" onClick={() => navigate(at(`/evaluations/${d.draft_id}`))}>
                          Open draft
                        </button>
                      ) : (
                        <button className="btn small primary" disabled={a.busy} onClick={() => start(d.id)}>
                          Start evaluation
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : !r.data.evaluations.length ? (
        <EmptyState icon="clipboard" title="No evaluations yet">Start one from the Due tab.</EmptyState>
      ) : (
        <div className="card table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>#</th>
                <th>Employee</th>
                <th>Kind</th>
                <th>Period</th>
                <th>Overall</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {r.data.evaluations.map((e: any) => (
                <tr key={e.id} className="clickable" onClick={() => navigate(at(`/evaluations/${e.id}`))}>
                  <td className="mono">{e.number}</td>
                  <td>{e.employee_name}</td>
                  <td>{r.data.kinds[e.kind]}</td>
                  <td>
                    {e.period_start ? day(e.period_start) : ''} – {e.period_end ? day(e.period_end) : ''}
                  </td>
                  <td>{e.overall ?? '—'}</td>
                  <td>
                    <Badge tone={EV_STATUS[e.status]![1]}>{EV_STATUS[e.status]![0]}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function EvaluationDetail({ id }: { id: string }) {
  const { navigate } = useRouter();
  const r = useLoad(() => api.get<any>(`/api/evaluations/${id}`), [id]);
  const a = useAction();
  const [f, setF] = useState<any>(null);
  const [ack, setAck] = useState({ name: '', comment: '' });
  const [note, setNote] = useState('');
  const v = r.data;
  useEffect(() => {
    if (v && !f) setF({ ratings: v.evaluation.ratings, strengths: v.evaluation.strengths ?? '', improvements: v.evaluation.improvements ?? '', goals: v.evaluation.goals ?? '' });
  }, [v, f]);
  if (!v || !f) return <div className="ops stack">{r.error ? <ErrorBox error={r.error} /> : <Spinner />}</div>;
  const e = v.evaluation;
  const ro = !v.canEdit;
  const save = () => a.run(async () => (await api.post(`/api/evaluations/${id}`, f), r.reload()));
  const SCALE = ['', 'Unsatisfactory', 'Needs improvement', 'Meets standards', 'Exceeds standards', 'Outstanding'];
  return (
    <div className="ops stack">
      <PageHeader crumb={crumb('Evaluations', '/evaluations', navigate)} title={`${e.number} · ${e.employee_name}`} sub={`${v.kinds[e.kind]} evaluation by ${e.evaluator_name}${e.period_end ? ` · ${day(e.period_start)} – ${day(e.period_end)}` : ''}`} actions={<Badge tone={EV_STATUS[e.status]![1]}>{EV_STATUS[e.status]![0]}</Badge>} />
      <ErrorBox error={a.error} />
      <Card title={`Ratings${e.overall ? ` · overall ${e.overall}` : ''}`}>
        <div className="ev-grid">
          {f.ratings.map((rt: any, i: number) => (
            <div key={rt.category} className="ev-row">
              <strong>{rt.category}</strong>
              <div className="ev-scale" role="radiogroup" aria-label={rt.category}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <label key={n} className={`ev-pt${rt.score === n ? ' on' : ''}`} title={SCALE[n]}>
                    <input type="radio" name={`r${i}`} disabled={ro} checked={rt.score === n} onChange={() => setF({ ...f, ratings: f.ratings.map((x: any, j: number) => (j === i ? { ...x, score: n } : x)) })} aria-label={`${n} — ${SCALE[n]}`} />
                    {n}
                  </label>
                ))}
              </div>
              <input disabled={ro} value={rt.comment ?? ''} onChange={(ev) => setF({ ...f, ratings: f.ratings.map((x: any, j: number) => (j === i ? { ...x, comment: ev.target.value } : x)) })} placeholder="Comment (examples)" aria-label={`${rt.category} comment`} />
            </div>
          ))}
        </div>
        <p className="small muted">1 Unsatisfactory · 2 Needs improvement · 3 Meets standards · 4 Exceeds standards · 5 Outstanding</p>
      </Card>
      <div className="grid2 top">
        <Card title="Strengths">
          <textarea rows={4} disabled={ro} value={f.strengths} onChange={(ev) => setF({ ...f, strengths: ev.target.value })} aria-label="Strengths" />
        </Card>
        <Card title="Areas to improve">
          <textarea rows={4} disabled={ro} value={f.improvements} onChange={(ev) => setF({ ...f, improvements: ev.target.value })} aria-label="Areas to improve" />
        </Card>
      </div>
      <Card title="Goals for next period">
        <textarea rows={3} disabled={ro} value={f.goals} onChange={(ev) => setF({ ...f, goals: ev.target.value })} aria-label="Goals" />
      </Card>
      {v.canEdit && (
        <div className="row gap">
          <button className="btn primary" disabled={a.busy} onClick={() => a.run(async () => (await api.post(`/api/evaluations/${id}`, f), await api.post(`/api/evaluations/${id}/submit`, {}), setF(null), r.reload()))}>
            Send to {e.employee_name} to sign
          </button>
          <button className="btn" disabled={a.busy} onClick={save}>
            Save draft
          </button>
        </div>
      )}
      {e.ack_at && (
        <Card title="Employee">
          <p>
            Signed by {e.ack_name}, {when(e.ack_at)}
          </p>
          {e.ack_comment && <p className="small pre">Comments: {e.ack_comment}</p>}
        </Card>
      )}
      {e.reviewed_at && (
        <Card title="Review">
          <p>
            Final — {e.reviewer_name}, {when(e.reviewed_at)}
          </p>
          {e.review_note && <p className="small pre">{e.review_note}</p>}
        </Card>
      )}
      {v.canAck && (
        <Card title="Sign">
          <form
            className="stack sm"
            onSubmit={(ev) => {
              ev.preventDefault();
              a.run(async () => (await api.post(`/api/evaluations/${id}/acknowledge`, { name: ack.name, comment: ack.comment || null }), r.reload()));
            }}
          >
            <p className="small">Signing means you’ve read it; it doesn’t mean you agree. Add anything you want on the record.</p>
            <label>
              Type your name to sign
              <input value={ack.name} required onChange={(ev) => setAck({ ...ack, name: ev.target.value })} />
            </label>
            <label>
              Your comments (optional)
              <textarea rows={3} value={ack.comment} onChange={(ev) => setAck({ ...ack, comment: ev.target.value })} />
            </label>
            <div>
              <button className="btn primary" disabled={a.busy}>
                Sign
              </button>
            </div>
          </form>
        </Card>
      )}
      {v.canReview && (
        <Card title="Second-level review">
          <div className="stack sm">
            <textarea rows={2} value={note} onChange={(ev) => setNote(ev.target.value)} placeholder="Note (optional)" aria-label="Review note" />
            <div>
              <button className="btn primary" disabled={a.busy} onClick={() => a.run(async () => (await api.post(`/api/evaluations/${id}/review`, { note: note || null }), r.reload()))}>
                Make it final
              </button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}

// ─── Vehicle inspections ────────────────────────────────────────────────────
export function InspectionsPage() {
  const { navigate } = useRouter();
  const [scope, setScope] = useState<'mine' | 'due' | 'failed' | 'all'>('mine');
  const r = useLoad(() => api.get<any>(`/api/inspections?scope=${scope}`), [scope]);
  return (
    <div className="ops stack">
      <PageHeader crumb="Equipment" title="Vehicle inspections" sub="Inspect your car every 30 days. Anything wrong goes straight to the quartermaster." />
      {r.data?.canSeeAll && (
        <div className="seg-ctl" role="tablist" aria-label="Which cars">
          {(['mine', 'due', 'failed', 'all'] as const).map((k) => (
            <button key={k} type="button" role="tab" aria-selected={scope === k} className={scope === k ? 'on' : ''} onClick={() => setScope(k)}>
              {{ mine: 'My car', due: 'Due', failed: 'Last one failed', all: 'Every car' }[k]}
            </button>
          ))}
        </div>
      )}
      {r.error ? (
        <ErrorBox error={r.error} />
      ) : !r.data ? (
        <Spinner />
      ) : !r.data.vehicles.length ? (
        <EmptyState icon="car" title={scope === 'mine' ? 'No car assigned to you' : 'None'} />
      ) : (
        <div className="fi-list">
          {r.data.vehicles.map((x: any) => (
            <div key={x.id} className="card fi-item insp-car">
              <div className="fi-top">
                <strong>
                  Car {x.unit_number} · {[x.year, x.make, x.model].filter(Boolean).join(' ')}
                </strong>
                {x.due ? <Badge tone="warn">Inspection due</Badge> : <Badge tone="ok">Inspected {day(x.last_at)}</Badge>}
              </div>
              <div className="small muted">
                {x.assigned_name ? `Assigned to ${x.assigned_name}` : 'Spare'}
                {x.last_at ? ` · last inspection ${day(x.last_at)} by ${x.last_by} — ${x.last_passed ? 'passed' : 'problems found'}` : ' · never inspected'}
              </div>
              <div className="row gap">
                <button className="btn primary small" onClick={() => navigate(at(`/inspections/${x.id}/new`))}>
                  Inspect now
                </button>
                <button className="btn small" onClick={() => navigate(at(`/inspections/${x.id}`))}>
                  History
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function NewInspection({ vehicleId }: { vehicleId: string }) {
  const { navigate } = useRouter();
  const r = useLoad(() => api.get<any>(`/api/fleet/${vehicleId}/inspections`), [vehicleId]);
  const [items, setItems] = useState<{ item: string; ok: boolean; note: string }[] | null>(null);
  const [f, setF] = useState({ mileage: '', defects: '', outOfService: false });
  const [photo, setPhoto] = useState<File | null>(null);
  const a = useAction();
  useEffect(() => {
    if (r.data && !items) setItems(r.data.items.map((i: string) => ({ item: i, ok: true, note: '' })));
  }, [r.data, items]);
  if (!items) return <div className="ops stack">{r.error ? <ErrorBox error={r.error} /> : <Spinner />}</div>;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    a.run(async () => {
      await api.post(`/api/fleet/${vehicleId}/inspections`, { items, mileage: f.mileage ? Number(f.mileage) : null, defects: f.defects || null, outOfService: f.outOfService, ...(photo ? { photoBase64: await fileToBase64(photo) } : {}) });
      navigate(at('/inspections'), true);
    });
  };
  const bad = items.filter((i) => !i.ok).length;
  return (
    <div className="ops stack">
      <PageHeader crumb={crumb('Vehicle inspections', '/inspections', navigate)} title="Inspect the car" sub="Uncheck anything that isn’t right and say what’s wrong." />
      <form className="stack" onSubmit={submit}>
        <ErrorBox error={a.error} />
        <Card>
          <div className="insp-list">
            {items.map((it, i) => (
              <div key={it.item} className={`insp-item${it.ok ? '' : ' bad'}`}>
                <label className="check">
                  <input type="checkbox" checked={it.ok} onChange={(e) => setItems(items.map((x, j) => (j === i ? { ...x, ok: e.target.checked } : x)))} /> {it.item}
                </label>
                {!it.ok && <input value={it.note} required onChange={(e) => setItems(items.map((x, j) => (j === i ? { ...x, note: e.target.value } : x)))} placeholder="What’s wrong?" aria-label={`${it.item} problem`} />}
              </div>
            ))}
          </div>
        </Card>
        <Card>
          <div className="stack sm">
            <label>
              Mileage
              <input type="number" min={0} value={f.mileage} onChange={(e) => setF({ ...f, mileage: e.target.value })} />
            </label>
            <label>
              Anything else
              <textarea rows={2} value={f.defects} onChange={(e) => setF({ ...f, defects: e.target.value })} />
            </label>
            <FileDrop accept="image/jpeg,image/png" file={photo} onFile={setPhoto} label="Photo of a problem (optional)" hint="JPEG or PNG" />
            {bad > 0 && (
              <label className="check">
                <input type="checkbox" checked={f.outOfService} onChange={(e) => setF({ ...f, outOfService: e.target.checked })} /> It isn’t safe to drive — should come out of service
              </label>
            )}
          </div>
        </Card>
        <div>
          <button className="btn primary" disabled={a.busy}>
            {bad ? `Save — ${bad} problem${bad === 1 ? '' : 's'}` : 'Save — everything passed'}
          </button>
        </div>
      </form>
    </div>
  );
}

export function InspectionHistory({ vehicleId }: { vehicleId: string }) {
  const { navigate } = useRouter();
  const r = useLoad(() => api.get<any>(`/api/fleet/${vehicleId}/inspections`), [vehicleId]);
  return (
    <div className="ops stack">
      <PageHeader crumb={crumb('Vehicle inspections', '/inspections', navigate)} title="Inspection history" />
      {r.error ? (
        <ErrorBox error={r.error} />
      ) : !r.data ? (
        <Spinner />
      ) : !r.data.inspections.length ? (
        <EmptyState icon="car" title="Never inspected" />
      ) : (
        r.data.inspections.map((x: any) => (
          <Card key={x.id} title={`${when(x.at)} · ${x.inspector}`} actions={x.passed ? <Badge tone="ok">Passed</Badge> : <Badge tone="bad">{x.out_of_service ? 'Out of service' : 'Problems'}</Badge>}>
            <div className="small">{x.mileage ? `${Number(x.mileage).toLocaleString()} miles` : ''}</div>
            {(x.items as any[]).filter((i) => !i.ok).length > 0 && (
              <ul className="small">
                {(x.items as any[])
                  .filter((i) => !i.ok)
                  .map((i) => (
                    <li key={i.item}>
                      <strong>{i.item}:</strong> {i.note}
                    </li>
                  ))}
              </ul>
            )}
            {x.defects && <p className="small">{x.defects}</p>}
            {x.has_photo && <img className="cw-thumb" src={`/api/inspections/${x.id}/photo`} alt="Inspection photo" />}
          </Card>
        ))
      )}
    </div>
  );
}
